// src/components/ReportHeader/index.js
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper } from '@mui/material';

/**
 * Modern, flexible report header component
 * Built for multi-tenant architecture with maintainability first
 * 
 * Design Principles:
 * - Maintainability over performance
 * - Multi-tenant support via client theming
 * - Minimal dependencies (uses only MUI core)
 * - Clean, composable API
 * - Fixed desktop layout: 1/3 title + 2/3 controls (subdivided)
 * 
 * @param {Object} props
 * @param {string|React.ReactNode} props.title - Report title (string or custom component)
 * @param {string} props.subtitle - Optional subtitle
 * @param {React.ReactNode[]} props.leftControls - Controls positioned on the left (after title)
 * @param {React.ReactNode[]} props.centerControls - Controls positioned in the center
 * @param {React.ReactNode[]} props.rightControls - Controls positioned on the right
 * @param {Object} props.theme - Client-specific theme configuration
 * @param {string} props.theme.backgroundColor - Header background color
 * @param {string} props.theme.titleColor - Title text color
 * @param {string} props.theme.textColor - General text color
 * @param {number|string} props.minWidth - Minimum width for the header (default: 'auto')
 * @param {number|string} props.maxWidth - Maximum width for the header (default: 'none')
 * @param {Object} props.sx - Custom styling (MUI sx prop)
 */
function ReportHeader({
  title,
  subtitle,
  leftControls = [],
  centerControls = [],
  rightControls = [],
  theme = {},
  minWidth = 'auto',
  maxWidth = 'none',
  sx = {}
}) {
  // Default theme that matches your existing design
  const defaultTheme = {
    backgroundColor: '#2c3e50',
    titleColor: '#f1c40f',
    textColor: '#ffffff',
    ...theme
  };

  // Render title section
  const renderTitle = () => {
    if (typeof title === 'string') {
      return (
        <Box sx={{ width: '100%' }}>
          <Typography 
            variant="h5" 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: defaultTheme.titleColor,
              mb: subtitle ? 0.5 : 0,
              lineHeight: 1.2,
              wordWrap: 'break-word',
              hyphens: 'auto'
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography 
              variant="body1" 
              sx={{ 
                color: defaultTheme.textColor,
                opacity: 0.8,
                lineHeight: 1.3,
                wordWrap: 'break-word'
              }}
            >
              {subtitle}
            </Typography>
          )}
        </Box>
      );
    }
    
    // Custom title component
    return title;
  };

  // Render control groups
  const renderControls = (controls, key) => {
    if (!controls || controls.length === 0) return null;
    
    return (
      <Box 
        key={key}
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          flexWrap: 'wrap'
        }}
      >
        {controls.map((control, index) => (
          <Box key={`${key}-${index}`}>
            {control}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: defaultTheme.backgroundColor,
        color: defaultTheme.textColor,
        px: 3,
        py: 2,
        borderRadius: 0,
        minWidth: minWidth, // Use configurable minWidth
        maxWidth: maxWidth, // Use configurable maxWidth
        width: '100%',
        margin: maxWidth !== 'none' ? '0 auto' : undefined, // Center when maxWidth is set
        overflow: 'hidden', // Prevent any overflow
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          minHeight: '60px',
          width: '100%',
          // Removed minWidth from here since it's now on Paper
        }}
      >
        {/* Title Section - 1/3 of space */}
        <Box sx={{ 
          width: '33.333%',
          display: 'flex',
          alignItems: 'center',
          pr: 2, // Padding right to separate from controls
          minWidth: 0 // Allow content to wrap
        }}>
          {renderTitle()}
        </Box>

        {/* Controls Section - 2/3 of space, subdivided into 3 parts */}
        <Box sx={{
          width: '66.667%',
          display: 'flex',
          alignItems: 'center',
          minHeight: '60px',
          overflow: 'hidden' // Prevent controls from overflowing
        }}>
          {/* Left Controls - 1/3 of controls area */}
          <Box sx={{
            width: '33.333%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: 2,
            px: 1 // Small padding
          }}>
            {renderControls(leftControls, 'left')}
          </Box>

          {/* Center Controls - 1/3 of controls area */}
          <Box sx={{
            width: '33.333%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            px: 1 // Small padding
          }}>
            {renderControls(centerControls, 'center')}
          </Box>

          {/* Right Controls - 1/3 of controls area */}
          <Box sx={{
            width: '33.333%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 1, // Reduced gap
            pl: 1,
            pr: 0,
            mr: 2,
            overflow: 'hidden', // Prevent overflow
            maxWidth: '100%' // Ensure it doesn't exceed container
          }}>
            {renderControls(rightControls, 'right')}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

// PropTypes for development-time validation
ReportHeader.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  subtitle: PropTypes.string,
  leftControls: PropTypes.arrayOf(PropTypes.node),
  centerControls: PropTypes.arrayOf(PropTypes.node),
  rightControls: PropTypes.arrayOf(PropTypes.node),
  theme: PropTypes.shape({
    backgroundColor: PropTypes.string,
    titleColor: PropTypes.string,
    textColor: PropTypes.string
  }),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object
};

export default ReportHeader;